import React, { useState, useEffect, useMemo } from "react";
import { Drawer, Box, Checkbox, Divider, FormControlLabel } from "@mui/material";
import cross from "../../../assets/images/refresh-cw 1.svg";
import StarFilled from "../../../assets/images/StarFilled.svg";
import StarOutlined from "../../../assets/images/StarOutline.svg";
import SnackBarMessage from "Samarth Module/components/snackbar/snackbar";
import CustomButton from "Samarth Module/components/buttons/CustomButton";
import styles from "Samarth Module/components/drawers/CustomiseColumns.module.css";
import PropTypes from "prop-types";

const MoreFilters = ({
  anchor,
  filterDrawer,
  handleFilterDrawer,
  rmpmFilters,
  setRmpmFilters,
  options,
  onFiltersSaved,
  limit = 9,
}) => {
  const [localFilters, setLocalFilters] = useState([...rmpmFilters]);
  const [selectedKey, setSelectedKey] = useState(rmpmFilters[0]?.label ?? "");
  const [displayData, setDisplayData] = useState(
    localFilters.filter((filter) => filter.isFavourite),
  );
  const [snackBarObject, setSnackBarObject] = useState({
    snackbarOpen: false,
    message: " ",
    type: "error",
  });

  useEffect(() => {
    setLocalFilters([...rmpmFilters]);
  }, [rmpmFilters]);

  useEffect(() => {
    setDisplayData(localFilters.filter((filter) => filter.isFavourite));
  }, [localFilters]);

  const filtersOrdered = useMemo(
    () => [...localFilters].sort((a, b) => (a.DisplayOrder ?? 0) - (b.DisplayOrder ?? 0)),
    [localFilters],
  );

  const closeDrawer = () => handleFilterDrawer();

  const closeSnackbar = () => {
    setSnackBarObject({ message: "", snackbarOpen: false, type: "error" });
  };

  const handleFavoriteUpdate = (index) => {
    setLocalFilters((prevFilters) => {
      const currentFavoritesCount = prevFilters.filter((f) => f.isFavourite).length;
      const isAddingFavorite = !prevFilters[index].isFavourite;

      if (isAddingFavorite && currentFavoritesCount >= limit) {
        setSnackBarObject({
          snackbarOpen: true,
          message: `You can't add more than ${limit} favorite filters.`,
          type: "error",
        });
        return prevFilters;
      }

      return prevFilters.map((filter, i) =>
        i === index ? { ...filter, isFavourite: !filter.isFavourite } : filter,
      );
    });
  };

  const getUpdatedSelectedData = (selectedData, value) =>
    selectedData.includes(value)
      ? selectedData.filter((item) => item !== value)
      : [...selectedData, value];

  const updateFilter = (filter, selectedKey, value) => {
    if (filter.label !== selectedKey) {
      return filter;
    }

    return {
      ...filter,
      selectedData: getUpdatedSelectedData(
        filter.selectedData,
        value,
      ),
    };
  };

  const handleFilterSelectionChange = (value) => {
    setLocalFilters((prevFilters) =>
      prevFilters.map((filter) =>
        updateFilter(filter, selectedKey, value),
      ),
    );
  };

  const handleApply = () => {
    setRmpmFilters([...localFilters]);
    if (onFiltersSaved) {
      onFiltersSaved([...localFilters]);
    }
    closeDrawer();
  };

  const currentValues =
    options && Object.keys(options).length > 0 ? options[selectedKey] || [] : [];

  return (
    <div className={styles.drawerContainer}>
      <Drawer
        anchor={anchor}
        open={filterDrawer}
        sx={{ "& .MuiDrawer-paper": { width: "35vw" } }}
      >
        <div className={styles.heading}>
          <span>More Filters</span>
          <button
            type="button"
            className={styles.closeButton}
            onClick={closeDrawer}
            aria-label="Close drawer"
          >
            <img
              src={cross}
              className={styles.closeIcon}
              alt=""
              aria-hidden="true"
            />
          </button>
        </div>

        <div className={styles.drawerContent}>
          <div className={styles.contentText}>
            Explore more filter options in this panel. Click the star icon to mark a filter as a
            favorite - up to {limit} filters will appear on the landing page. <br />
            &nbsp;• To update favorites, uncheck a starred filter and select a new one. <br />
          </div>
        </div>

        <Box className={styles.filterContainer}>
          <Box className={styles.keyContainer}>
            {filtersOrdered.map((filter) => {
              const isFavorite = displayData.some(
                (favorite) => favorite.label === filter.label,
              );

              const filterIndex = localFilters.findIndex(
                (localFilter) => localFilter.label === filter.label,
              );

              const filterClasses = [
                styles.filterKeys,
                selectedKey === filter.label ? styles.selectedKey : "",
              ]
                .filter(Boolean)
                .join(" ");


              return (
                <div
                  key={filter.label}
                  className={filterClasses}
                >
                  <button
                    type="button"
                    className={styles.starButton}
                    onClick={() => {
                      if (filterIndex >= 0) {
                        handleFavoriteUpdate(filterIndex);
                      }
                    }}
                  >
                    <img
                      src={isFavorite ? StarFilled : StarOutlined}
                      alt=""
                      className={styles.starIcon}
                      aria-hidden="true"
                    />
                  </button>

                  <button
                    type="button"
                    className={styles.filterLabelButton}
                    aria-pressed={selectedKey === filter.label}
                    onClick={() => setSelectedKey(filter.label)}
                  >
                    {filter.label}
                  </button>
                </div>
              );
            })}
          </Box>

          <Divider orientation="vertical" className={styles.divider} />

          <Box className={styles.valueContainer}>
            {currentValues.map((value, i) => (
              <FormControlLabel
                key={`${selectedKey}-${value}`}
                sx={{
                  margin: "0",
                  padding: "5px 15px",
                  "& .MuiFormControlLabel-label": { fontSize: "12px", marginLeft: "5px" },
                }}
                control={
                  <Checkbox
                    sx={{ transform: "scale(0.8)", padding: "0" }}
                    checked={
                      localFilters
                        .find((f) => f.label === selectedKey)
                        ?.selectedData?.includes(value) || false
                    }
                    onChange={() => handleFilterSelectionChange(value)}
                  />
                }
                label={value}
              />
            ))}
          </Box>
        </Box>

        <div className={styles.drawerBtns}>
          <CustomButton label="Cancel" type="mm-save" onClick={closeDrawer} />
          <CustomButton label="Apply" type="mm-submit" onClick={handleApply} />
        </div>
      </Drawer>

      {snackBarObject.snackbarOpen && (
        <SnackBarMessage
          type={snackBarObject.type}
          snackbarMessage={snackBarObject.message}
          snackbarOpen={snackBarObject.snackbarOpen}
          setSnackbarOpen={closeSnackbar}
        />
      )}
    </div>
  );
};

MoreFilters.propTypes = {
  anchor: PropTypes.oneOf([
    "left",
    "right",
    "top",
    "bottom",
  ]).isRequired,

  filterDrawer: PropTypes.bool.isRequired,

  handleFilterDrawer: PropTypes.func.isRequired,

  rmpmFilters: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      isFavourite: PropTypes.bool,
      DisplayOrder: PropTypes.number,
      selectedData: PropTypes.arrayOf(
        PropTypes.oneOfType([
          PropTypes.string,
          PropTypes.number,
        ])
      ),
    })
  ).isRequired,

  setRmpmFilters: PropTypes.func.isRequired,

  options: PropTypes.objectOf(
    PropTypes.arrayOf(
      PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
      ])
    )
  ),

  onFiltersSaved: PropTypes.func,

  limit: PropTypes.number,
};

export default MoreFilters;
